<script setup lang="ts">

import type { ConfigureConditionViewModel } from "..";
import { computed, ref, toRaw } from "vue";
import { comma } from "@library/Utilities";
import { useConfirm } from "@library/Components";
import { ServiceIntervalState, type IServiceInterval } from "@domain/Vehicle";
import { useConditionApi } from "@app/MyGarage";
import { isDarkMode } from '@services/Theme';

const model = defineModel<ConfigureConditionViewModel>( { required: true } );
const initiallyUnconfigured = toRaw( model.value.condition.isUnconfigured );
const schedule = model.value.vehicleCondition.serviceSchedule;
const estimatedNextInterval = schedule.getNextIntervalForOdometer( model.value.condition.odometer.current );
const selectedInterval = ref<IServiceInterval>( schedule.nextInterval ?? estimatedNextInterval );
const isConfirmed = ref( schedule.nextInterval != null );
const isSelected = ( interval: IServiceInterval ) => interval.km == selectedInterval.value.km;
const isBeforeSelected = ( interval: IServiceInterval ) => interval.km < ( selectedInterval.value.km ?? 0 );
const hasSelection = computed( () => selectedInterval.value != null );

/**
 * Occurs when the user selects or de-selects an interval
 * @param index 
 * @param interval 
 */
async function onToggleInterval( index: number, interval: IServiceInterval )
{

    if( await confirmSelection( index, interval ) == false ) return;

    selectedInterval.value = interval;
    isConfirmed.value = true;

    model.value.setServiceInterval( interval, schedule );
}

/**
 * Occurs when the service is done checkbox gets toggle
 * @param value 
 */
async function onToggleIsDone( interval: IServiceInterval )
{
    // Update the interval on the API
    const api = useConditionApi();
    api.setServiceIntervalState( interval.serviceScheduleId, interval.serviceIntervalId, interval.state );
}

/**
 * Ensure the selection makes sense and prompt the user to confirm if not
 * @param index 
 * @param interval 
 */
async function confirmSelection( index: number, interval: IServiceInterval )
{
    const completedIntervalsAfterSelection = schedule.intervals.filter( i => i.km > interval.km && i.state != ServiceIntervalState.NotDone );
    if( completedIntervalsAfterSelection.length > 0 )
    {
        const result = await useConfirm( "This interval is earlier than a service that is marked as completed.  If you select it, the later serice will be marked as incomplete.  Are you sure?", "Please confirm" );
        if( result )
        {
            completedIntervalsAfterSelection.forEach( i => i.state = ServiceIntervalState.NotDone );
        }
        return result;
    }
    return true;
}

</script>

<template>

    <div class="text-base text-center flex flex-col pb-10 ">

        <!-- THE CONDITION STATE HAS NOT PREVIOUSLY BEEN SET -->
        <div v-if="initiallyUnconfigured"
             class="my-4 px-6 lg:px-8 tracking-wider">
            <h1>
                Based on your odometer of {{ comma( model.condition.odometer.current ) }}km, 
                we've estimated your next service interval.
            </h1>
            <p class="opacity-60 mt-2">
                If this is incorrect, select the correct one and mark off any that are completed
            </p>
        </div>
        
        <div v-else
             class="my-4 px-6 lg:px-8 tracking-wider">
            <h1>
                If this has changed, update it and remember to mark any that are complete
            </h1>
        </div>
        
        <Spacer />
            
        <h1 class="text-lg mt-4">Service Intervals</h1>

        <!-- HEADER ROW -->
        <div class="grid grid-cols-12 h-14 items-center uppercase tracking-wider">
            <div class="col-span-2 text-left pl-4 lg:pl-6 opacity-60">#</div>
            <div class="col-span-4 text-left opacity-60">Months</div>
            <div class="col-span-4 text-left opacity-60">KM's</div>
            <div class="col-span-2 pr-2 opacity-60">Done</div>
        </div>

        <!--SPACER -->
        <ContentSpacer class="border-b border-gray-800" />

        <!-- INTERVALS -->
        <div v-for="(interval, index) in schedule.intervals"
             class="grid grid-cols-12 h-16 items-center tracking-wider cursor-pointer"
             :class="{
                 'bg-gray-700/70 !border-gray-600/70 text-white': isSelected( interval ) && isConfirmed == false,
                 'bg-primary-500 dark:bg-secondary-500 !border-secondary-400 text-white': isSelected( interval ) && isConfirmed 
             }"
             @click="() => onToggleInterval( index, interval )">
                                
            <div class="col-span-2 text-left pl-4 lg:pl-6 h-full flex items-center">
                <Icon v-if="( interval.km == selectedInterval.km && hasSelection == false ) || isSelected( interval )" name="car" class="text-2xl -ml-2" />
                <span v-else-if="isBeforeSelected( interval )" class="opacity-60">-</span>
                <!-- <div v-if="isBeforeSelected( interval ) || isSelected( interval )"
                     class="absolute w-[4px] top-0 bottom-0 left-6 border-r-2 border-dashed"
                     :class="{ 'top-[40%]': index == 0, '!bottom-[95%]': isSelected( interval ) }">
                    <div v-if="index == 0" class="absolute rounded-full dark:bg-white w-3 h-3 -left-[3px] -top-1"></div>
                    <div v-if="isSelected( interval )" class="absolute rounded-full dark:bg-white w-3 h-3 -left-[3px] -bottom-1"></div>
                </div> -->
            </div>

            <div class="col-span-8">
                <div class="grid grid-cols-2">
                    <div class="col-span-1 text-left">
                        {{ interval.months }} months
                    </div>
                    
                    <div class="col-span-1 text-left">
                        {{ comma( interval.km ) }} km
                    </div> 
                    <div v-if="isBeforeSelected( interval ) || isSelected( interval )"
                         class="col-span-2 text-xs opacity-60 leading-tight text-left -mt-0.5">
                        <span v-if="interval.state != ServiceIntervalState.NotDone">Done</span>
                        <span v-else-if="isSelected( interval ) && isConfirmed && interval.state == ServiceIntervalState.NotDone">This will be your next service</span>
                        <span v-else-if="isSelected( interval ) && interval.state == ServiceIntervalState.NotDone">Your estimated next service</span>
                        <span v-else-if="isBeforeSelected( interval ) && interval.state == ServiceIntervalState.NotDone">Not done</span>
                        <span v-else>-</span>
                    </div>
                </div>
            </div>
            
            <div class="col-span-2 pr-2 text-center">
                <CheckBox v-if="isBeforeSelected( interval )"
                          :color="isDarkMode ? 'white' : 'black'"
                          class="inline-block" 
                          v-model="interval.isDone"
                          @change="() => onToggleIsDone( interval )"
                          @click.stop="() => {}" /> 
                <div v-else-if="isSelected( interval ) && isConfirmed" class="uppercase text-xs leading-tight">
                    <Icon name="calendar" class="text-2xl" />
                </div>
            </div>

        </div>

    </div>

</template>